import type { Skill } from '@/types';
import type { ChoiceEffect } from '@/types';

/**
 * Un skill tiene, además de los metadatos tipados en `Skill`, una lista
 * de efectos que se aplican al desbloquearlo. Reutilizamos el mismo
 * ChoiceEffect que usa el engine narrativo.
 */
export interface SkillDefinicion extends Skill {
  efectosAlDesbloquear?: ChoiceEffect[];
  /** Usable en combate como habilidad activa. */
  habilidadActiva?: {
    id: string;
    nombre: string;
    costoMana?: number;
    descripcionCorta: string;
  };
}

/** Rama Guardián: protector, tank, empático. */
const GUARDIAN: SkillDefinicion[] = [
  {
    id: 'postura_solida',
    rama: 'guardian',
    nombre: 'Postura Sólida',
    descripcion: 'Aprendés a plantarte. +3 de vida máxima y +1 de resistencia.',
    tier: 1,
    requisitos: [],
    costo: 1,
    posicion: { x: 0, y: 0 },
    efectosAlDesbloquear: [
      { kind: 'stat', stat: 'vidaMax', delta: 3 },
      { kind: 'stat', stat: 'vida', delta: 3 },
      { kind: 'stat', stat: 'resistencia', delta: 1 },
    ],
  },
  {
    id: 'empatia_del_arbol',
    rama: 'guardian',
    nombre: 'Empatía del Árbol',
    descripcion: 'Escuchás más de lo que decís. +2 de empatía.',
    tier: 1,
    requisitos: [],
    costo: 1,
    posicion: { x: 0, y: 1 },
    efectosAlDesbloquear: [{ kind: 'stat', stat: 'empatia', delta: 2 }],
  },
  {
    id: 'escudo_voluntad',
    rama: 'guardian',
    nombre: 'Escudo de Voluntad',
    descripcion:
      'Cuando protegés a alguien, algo en vos se convierte en pared. +6 de vida máx.',
    tier: 2,
    requisitos: ['postura_solida'],
    costo: 2,
    posicion: { x: 0, y: 2 },
    efectosAlDesbloquear: [
      { kind: 'stat', stat: 'vidaMax', delta: 6 },
      { kind: 'stat', stat: 'vida', delta: 6 },
    ],
    habilidadActiva: {
      id: 'defender_firme',
      nombre: 'Defender Firme',
      descripcionCorta: 'Duplica tu defensa este turno.',
    },
  },
  {
    id: 'palabra_calmante',
    rama: 'guardian',
    nombre: 'Palabra Calmante',
    descripcion: 'Tu voz baja temperaturas. Aprendés el hechizo Susurro del Río.',
    tier: 2,
    requisitos: ['empatia_del_arbol'],
    costo: 2,
    posicion: { x: 0, y: 3 },
    efectosAlDesbloquear: [{ kind: 'unlockSpell', hechizoId: 'susurro_rio' }],
  },
  {
    id: 'corazon_roble',
    rama: 'guardian',
    nombre: 'Corazón de Roble',
    descripcion: 'Aguantás lo que otros no. +12 de vida máx y +1 resistencia.',
    tier: 3,
    requisitos: ['escudo_voluntad'],
    costo: 3,
    posicion: { x: 0, y: 4 },
    efectosAlDesbloquear: [
      { kind: 'stat', stat: 'vidaMax', delta: 12 },
      { kind: 'stat', stat: 'vida', delta: 12 },
      { kind: 'stat', stat: 'resistencia', delta: 1 },
    ],
  },
  {
    id: 'juramento_guardian',
    rama: 'guardian',
    nombre: 'Juramento del Guardián',
    descripcion:
      'Tomás la palabra que nadie puede romper. Abre el camino al final "El Guardián Eterno".',
    tier: 4,
    requisitos: ['corazon_roble', 'palabra_calmante'],
    costo: 4,
    posicion: { x: 0, y: 5 },
    efectosAlDesbloquear: [{ kind: 'flag', flagId: 'juramento_guardian_tomado', valor: true }],
  },
];

/** Rama Druida Capibárico: magia natural, vínculo animal, curación. */
const DRUIDA: SkillDefinicion[] = [
  {
    id: 'oido_bosque',
    rama: 'druida',
    nombre: 'Oído del Bosque',
    descripcion: 'Empezás a escuchar lo que no tiene voz. +1 de astucia y +3 de mana máx.',
    tier: 1,
    requisitos: [],
    costo: 1,
    posicion: { x: 1, y: 0 },
    efectosAlDesbloquear: [
      { kind: 'stat', stat: 'astucia', delta: 1 },
      { kind: 'stat', stat: 'manaMax', delta: 3 },
      { kind: 'stat', stat: 'mana', delta: 3 },
    ],
  },
  {
    id: 'sangre_raiz',
    rama: 'druida',
    nombre: 'Sangre de Raíz',
    descripcion: 'La savia también es sangre. +5 de mana máx.',
    tier: 1,
    requisitos: [],
    costo: 1,
    posicion: { x: 1, y: 1 },
    efectosAlDesbloquear: [
      { kind: 'stat', stat: 'manaMax', delta: 5 },
      { kind: 'stat', stat: 'mana', delta: 5 },
    ],
  },
  {
    id: 'curar_con_tacto',
    rama: 'druida',
    nombre: 'Curar con Tacto',
    descripcion: 'Aprendés el hechizo Vínculo Sanador.',
    tier: 2,
    requisitos: ['sangre_raiz'],
    costo: 2,
    posicion: { x: 1, y: 2 },
    efectosAlDesbloquear: [{ kind: 'unlockSpell', hechizoId: 'vinculo_sanador' }],
  },
  {
    id: 'vinculo_pipo',
    rama: 'druida',
    nombre: 'Vínculo con Pipo',
    descripcion: 'Pipo te entiende sin palabras. +3 de vínculo.',
    tier: 2,
    requisitos: ['oido_bosque'],
    costo: 2,
    posicion: { x: 1, y: 3 },
    efectosAlDesbloquear: [{ kind: 'stat', stat: 'vinculo', delta: 3 }],
  },
  {
    id: 'invocacion_susurrada',
    rama: 'druida',
    nombre: 'Invocación Susurrada',
    descripcion: 'Podés llamar a criaturas del bosque en combate.',
    tier: 3,
    requisitos: ['curar_con_tacto'],
    costo: 3,
    posicion: { x: 1, y: 4 },
    efectosAlDesbloquear: [
      { kind: 'stat', stat: 'manaMax', delta: 8 },
      { kind: 'stat', stat: 'mana', delta: 8 },
      { kind: 'unlockSpell', hechizoId: 'llamado_guardian' },
    ],
  },
  {
    id: 'descendiente_guardian',
    rama: 'druida',
    nombre: 'Descendiente del Guardián',
    descripcion: 'Aceptás lo que Pipo siempre supo. Abre el camino al final "Renacimiento".',
    tier: 4,
    requisitos: ['invocacion_susurrada', 'vinculo_pipo'],
    costo: 4,
    posicion: { x: 1, y: 5 },
    efectosAlDesbloquear: [{ kind: 'flag', flagId: 'descendiente_guardian_aceptado', valor: true }],
  },
];

/** Rama Estratega: astucia, persuasión, control. */
const ESTRATEGA: SkillDefinicion[] = [
  {
    id: 'palabra_aceitada',
    rama: 'estratega',
    nombre: 'Palabra Aceitada',
    descripcion: 'Sabés cuándo mentir y cuándo no. +2 de astucia.',
    tier: 1,
    requisitos: [],
    costo: 1,
    posicion: { x: 2, y: 0 },
    efectosAlDesbloquear: [{ kind: 'stat', stat: 'astucia', delta: 2 }],
  },
  {
    id: 'ojo_cuervo',
    rama: 'estratega',
    nombre: 'Ojo de Cuervo',
    descripcion: 'Ves lo que no miran. +1 astucia y +1 valentía.',
    tier: 1,
    requisitos: [],
    costo: 1,
    posicion: { x: 2, y: 1 },
    efectosAlDesbloquear: [
      { kind: 'stat', stat: 'astucia', delta: 1 },
      { kind: 'stat', stat: 'valentia', delta: 1 },
    ],
  },
  {
    id: 'lectura_enemigo',
    rama: 'estratega',
    nombre: 'Lectura del Enemigo',
    descripcion: 'En combate, esperás el momento. +10 vida máx y +1 valentía.',
    tier: 2,
    requisitos: ['ojo_cuervo'],
    costo: 2,
    posicion: { x: 2, y: 2 },
    efectosAlDesbloquear: [
      { kind: 'stat', stat: 'vidaMax', delta: 10 },
      { kind: 'stat', stat: 'vida', delta: 10 },
      { kind: 'stat', stat: 'valentia', delta: 1 },
    ],
    habilidadActiva: {
      id: 'golpe_calculado',
      nombre: 'Golpe Calculado',
      descripcionCorta: 'Tu próximo ataque tiene ventaja (tirás 2d20 y te quedás con el mayor).',
    },
  },
  {
    id: 'voz_mercader',
    rama: 'estratega',
    nombre: 'Voz del Mercader',
    descripcion: 'Nadie te miente sobre precios ni promesas. +2 astucia.',
    tier: 2,
    requisitos: ['palabra_aceitada'],
    costo: 2,
    posicion: { x: 2, y: 3 },
    efectosAlDesbloquear: [{ kind: 'stat', stat: 'astucia', delta: 2 }],
  },
  {
    id: 'artimana',
    rama: 'estratega',
    nombre: 'Artimaña',
    descripcion: 'Escaparte es un arte. Huir del combate es más fácil.',
    tier: 3,
    requisitos: ['voz_mercader'],
    costo: 3,
    posicion: { x: 2, y: 4 },
    efectosAlDesbloquear: [
      { kind: 'stat', stat: 'astucia', delta: 2 },
      { kind: 'flag', flagId: 'artimana_desbloqueada', valor: true },
    ],
  },
  {
    id: 'ajedrez_sangre',
    rama: 'estratega',
    nombre: 'Ajedrez de Sangre',
    descripcion:
      'Entendés el conflicto como un tablero. Abre el camino al final "La Corona de Sangre".',
    tier: 4,
    requisitos: ['artimana', 'lectura_enemigo'],
    costo: 4,
    posicion: { x: 2, y: 5 },
    efectosAlDesbloquear: [{ kind: 'flag', flagId: 'ajedrez_sangre_asumido', valor: true }],
  },
];

export const SKILLS_TODAS: readonly SkillDefinicion[] = [
  ...GUARDIAN,
  ...DRUIDA,
  ...ESTRATEGA,
];

const MAPA = new Map<string, SkillDefinicion>(SKILLS_TODAS.map((s) => [s.id, s]));

export function obtenerSkill(id: string): SkillDefinicion | undefined {
  return MAPA.get(id);
}

export function skillsPorRama(rama: SkillDefinicion['rama']): SkillDefinicion[] {
  return SKILLS_TODAS.filter((s) => s.rama === rama);
}
